// src/client/screens/auth/LoginScreen.tsx
// Two-step login: phone → OTP.
// VITE_DEV_LOGIN=true: phone step only — calls dev-login Edge Function to sign
// in immediately with a real session, no OTP required. Use for test accounts.
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { OTP_LENGTH } from '@/constants/config';
import Button from '@client/components/Button';
import { useToast } from '@client/components/ToastProvider';
import { sendOtp, verifyOtp, devLogin } from '@client/services/authService';
import { isValidZambiaPhone, normalizeZambiaPhone, formatZambiaPhone } from '@client/utils/validation';

// Safe read: import.meta.env is undefined under ts-jest/ESM.
type ViteMeta = { env?: Record<string, string | undefined> };
const DEV_LOGIN = ((import.meta as ViteMeta).env?.['VITE_DEV_LOGIN']) === 'true';

type Step = 'phone' | 'otp';

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

const SCREEN: React.CSSProperties = {
  minHeight: '100dvh',
  background: 'var(--c-bg)',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 480,
  margin: '0 auto',
};

export default function LoginScreen() {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();

  const handleSendOtp = async () => {
    if (!isValidZambiaPhone(phone)) {
      toast.show('Enter a valid +260 number', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (DEV_LOGIN) {
        const r = await devLogin(phone);
        if (!r.ok) {
          const msg = r.error === 'invalid_phone' ? 'Invalid phone number' : 'Dev sign-in failed';
          toast.show(msg, 'error');
          return;
        }
        const next = params.get('next') ?? ROUTES.home;
        navigate(next, { replace: true });
      } else {
        const r = await sendOtp(phone);
        if (!r.ok) {
          const msg =
            r.error === 'invalid_phone' ? 'That number is not valid' :
            r.error === 'rate_limited' ? 'Too many attempts — please wait' :
            'Could not send code — please try again';
          toast.show(msg, 'error');
          return;
        }
        toast.show('Code sent', 'success');
        setStep('otp');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    const normalized = normalizeZambiaPhone(phone);
    if (!normalized) { toast.show('Invalid phone', 'error'); return; }
    setSubmitting(true);
    try {
      const r = await verifyOtp(normalized, code);
      if (!r.ok) {
        const msg = r.error === 'invalid_code'
          ? 'That code is not correct'
          : r.error === 'invalid_phone'
          ? 'Phone number is not valid'
          : 'Could not verify';
        toast.show(msg, 'error');
        return;
      }
      const next = params.get('next') ?? ROUTES.home;
      navigate(next, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  const onBack = () => {
    if (step === 'otp') { setStep('phone'); setCode(''); return; }
    navigate(ROUTES.splash);
  };

  // Build the digit cells from the OTP code; first empty cell is highlighted
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => code[i] ?? '');
  const firstEmpty = digits.findIndex((d) => d === '');

  // Formatted phone for display on the OTP step
  const displayPhone = formatZambiaPhone(normalizeZambiaPhone(phone) ?? phone);

  return (
    <div style={SCREEN}>
      {/* Header — back chevron + step indicator */}
      <div style={{ padding: '14px 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
        <button type="button" aria-label="Go back" style={ICON_BTN} onClick={onBack}>
          <ChevronLeft size={18} />
        </button>
        <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 13, color: 'var(--c-text-muted)' }}>
          {step === 'phone' ? '01 / 02' : '02 / 02'}
        </span>
      </div>

      <div style={{ padding: '24px 28px', flex: 1 }}>
        {step === 'phone' ? (
          <>
            <h1 style={{
              fontFamily: 'var(--ff-display)',
              fontSize: 44,
              lineHeight: 1,
              color: 'var(--c-text)',
              letterSpacing: -1,
              fontWeight: 400,
              margin: 0,
            }}>
              What&apos;s your<br />number?
            </h1>
            <p style={{ marginTop: 14, fontSize: 14, color: 'var(--c-text-muted)' }}>
              We&apos;ll text a six-digit code. Standard rates apply.
            </p>

            <div style={{ marginTop: 36 }}>
              <label htmlFor="phone-input" className="eyebrow" style={{
                display: 'block', marginBottom: 10, letterSpacing: 1.2,
              }}>
                Mobile number
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                borderBottom: '1.5px solid var(--c-text)',
                paddingBottom: 10,
              }}>
                <span style={{
                  fontFamily: 'var(--ff-mono)',
                  fontSize: 22,
                  color: 'var(--c-text-muted)',
                }}>
                  +260
                </span>
                <input
                  id="phone-input"
                  inputMode="tel"
                  placeholder="97 412 3094"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--c-text)',
                    fontFamily: 'var(--ff-mono)',
                    fontSize: 22,
                    letterSpacing: 1,
                    padding: 0,
                  }}
                />
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--c-text-dim)', fontFamily: 'var(--ff-mono)' }}>
                ↳ MTN, Airtel, or Zamtel
              </div>
            </div>
          </>
        ) : (
          <>
            <h1 style={{
              fontFamily: 'var(--ff-display)',
              fontSize: 44,
              lineHeight: 1,
              color: 'var(--c-text)',
              letterSpacing: -1,
              fontWeight: 400,
              margin: 0,
            }}>
              Check your<br />messages.
            </h1>
            <p style={{ marginTop: 14, fontSize: 14, color: 'var(--c-text-muted)' }}>
              We sent a code to{' '}
              <span style={{ fontFamily: 'var(--ff-mono)', color: 'var(--c-text)' }}>
                {displayPhone}
              </span>.
            </p>

            {/* Visible digit cells — visually composed, but a hidden input captures
                actual keystrokes so paste / autofill / OS SMS suggestions still work. */}
            <div style={{ position: 'relative', marginTop: 36 }}>
              <input
                id="otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={OTP_LENGTH}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                aria-label={`Enter the ${OTP_LENGTH}-digit code`}
                style={{
                  position: 'absolute', inset: 0,
                  opacity: 0, width: '100%', height: '100%',
                  cursor: 'text',
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                {digits.map((d, i) => {
                  const isCursor = i === firstEmpty;
                  return (
                    <div key={i} style={{
                      flex: 1,
                      aspectRatio: '1',
                      maxHeight: 56,
                      border: `1px solid ${isCursor ? 'var(--c-accent)' : 'var(--c-border)'}`,
                      background: isCursor ? 'var(--c-accent-soft)' : 'var(--c-surface)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'var(--ff-mono)',
                      fontSize: 24,
                      color: 'var(--c-text)',
                      fontWeight: 500,
                    }}>
                      {d}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>Didn&apos;t get it?</span>
              <button
                type="button"
                onClick={() => void handleSendOtp()}
                style={{
                  background: 'transparent', border: 'none',
                  fontSize: 13, color: 'var(--c-text)',
                  fontFamily: 'var(--ff-mono)', cursor: 'pointer',
                  padding: 0,
                }}
              >
                resend code
              </button>
            </div>
          </>
        )}
      </div>

      <div style={{ padding: '16px 24px 28px' }}>
        <Button
          block size="lg" loading={submitting}
          onClick={() => step === 'phone' ? handleSendOtp() : handleVerify()}
          rightIcon={<ArrowRight size={18} />}
        >
          {step === 'phone' ? (DEV_LOGIN ? 'Sign in' : 'Send code') : 'Verify & continue'}
        </Button>
        {DEV_LOGIN && step === 'phone' && (
          <p style={{ margin: '10px 0 0', fontSize: 11, textAlign: 'center', color: 'var(--c-text-dim)' }}>
            Dev mode — OTP bypassed
          </p>
        )}
      </div>
    </div>
  );
}
